import {
  BadgeCheck,
  Clock,
  AlertTriangle,
  Send,
  MessageSquare,
  CalendarDays,
  Hourglass,
  ChevronLeft,
  ChevronRight,
  X,
  Users,
  User,
  Eye,
  Hash,
  Phone,
  FileText,
  Calendar,
  CheckCircle,
  XCircle,
  AlertCircle,
  Info,
} from "lucide-react";
import { useState } from "react";

const SmsDetailModal = ({ isOpen, onClose, smsData }) => {
  if (!isOpen || !smsData) return null;

  const getStatus = (item) => {
    if (item.type === "now") return "sent";
    if (item.type === "schedule") return "pending";
    return "unknown";
  };

  const status = getStatus(smsData);

  const statusIcon = (status) => {
    switch (status) {
      case "sent":
        return <CheckCircle className="text-emerald-600" size={16} />;
      case "pending":
        return <Clock className="text-amber-500" size={16} />;
      case "failed":
        return <XCircle className="text-red-500" size={16} />;
      default:
        return <AlertCircle className="text-gray-500" size={16} />;
    }
  };

  const statusColor = (status) => {
    switch (status) {
      case "sent":
        return "text-emerald-700 bg-emerald-50 border-emerald-200";
      case "pending":
        return "text-amber-700 bg-amber-50 border-amber-200";
      case "failed":
        return "text-red-700 bg-red-50 border-red-200";
      default:
        return "text-gray-700 bg-gray-50 border-gray-200";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-2">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[95vh] overflow-hidden border">
        {/* Header */}
        <div className="bg-[#4F22D8] text-white px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-white/20 rounded-lg">
              <MessageSquare size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">SMS Details</h2>
              <p className="text-white/80 text-xs mt-0.5">
                Message Information & Status
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white hover:bg-white/20 transition-all duration-200 p-1.5 rounded-lg"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(95vh-80px)]">
          <div className="space-y-4">
            {/* Status Section */}
            <div className="bg-gradient-to-r from-gray-100 to-gray-100 rounded-xl p-4 border">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <div
                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border font-semibold text-xs ${statusColor(
                      status
                    )}`}
                  >
                    {statusIcon(status)}
                    <span className="capitalize">{status}</span>
                  </div>
                  <div className="text-xs text-gray-600 font-medium">
                    {smsData.type === "schedule"
                      ? "📅 Scheduled Message"
                      : "⚡ Immediate Message"}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-0.5">
                    Message ID
                  </div>
                  <div className="font-mono text-xs font-semibold text-gray-700 bg-white px-2 py-1 rounded-md border">
                    {smsData._id}
                  </div>
                </div>
              </div>
            </div>

            {/* Template & Campaign Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="group relative bg-[#4F22D8] border-0 rounded-xl p-0.5 transition-all duration-300">
                <div className="bg-white rounded-xl p-3 h-full">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 flex justify-center items-center bg-[#4F22D8] rounded-lg shadow-lg">
                      <Hash className="text-white" size={14} />
                    </div>
                    <div>
                      <span className="text-xs font-bold text-gray-500 uppercase tracking-widest">
                        Template ID
                      </span>
                      <div className="w-6 h-0.5 bg-[#4F22D8] rounded-full mt-0.5"></div>
                    </div>
                  </div>
                  <div className="relative">
                    <div className="font-mono text-lg font-black text-transparent bg-clip-text bg-[#4F22D8] mb-1">
                      {smsData.templateId}
                    </div>
                    <div className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-[#4F22D8] rounded-full animate-pulse"></div>
                  </div>
                  <div className="flex items-center gap-1 mt-2">
                    <div className="flex-1 h-0.5 bg-gradient-to-r from-[#4F22D8] to-purple-100 rounded-full">
                      <div className="h-full w-4/5 bg-gradient-to-r from-[#4F22D8] to-[#4F22D8] rounded-full"></div>
                    </div>
                    <span className="text-xs font-semibold text-[#4F22D8]">
                      Active
                    </span>
                  </div>
                </div>
              </div>

              <div className="group relative bg-green rounded-xl p-0.5 hover:shadow-xl hover:shadow-emerald-500/25 transition-all duration-300">
                <div className="bg-white rounded-xl p-3 h-full">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 flex justify-center items-center bg-green rounded-lg shadow-lg">
                      <Send className="text-white" size={14} />
                    </div>
                    <div>
                      <span className="text-xs font-bold text-gray-500 uppercase tracking-widest">
                        Campaign ID
                      </span>
                      <div className="w-6 h-0.5 bg-green rounded-full mt-0.5"></div>
                    </div>
                  </div>
                  <div className="relative">
                    <div className="font-mono text-lg font-black text-transparent bg-clip-text bg-green mb-1">
                      {smsData.responseData?.campg_id || "N/A"}
                    </div>
                    <div className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-green rounded-full animate-pulse"></div>
                  </div>
                  <div className="flex items-center gap-1 mt-2">
                    <div className="flex-1 h-0.5 bg-gradient-to-r from-[green] to-teal-100 rounded-full">
                      <div className="h-full w-3/4 bg-gradient-to-r from-[green] to-[green] rounded-full"></div>
                    </div>
                    <span className="text-xs font-semibold text-emerald-600">
                      Running
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Recipients Section */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4 border">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 flex justify-center items-center bg-gray-600 rounded-lg">
                  {smsData.isGroup ? (
                    <Users className="text-white" size={14} />
                  ) : (
                    <User className="text-white" size={14} />
                  )}
                </div>
                <span className="font-semibold text-gray-900 text-sm">
                  Recipients
                </span>
              </div>
              <div className="space-y-2">
                {smsData.isGroup && (
                  <div className="flex items-center gap-2 text-xs">
                    <span className="px-3 py-1 bg-[#4F22D8] text-white rounded-xl font-semibold">
                      📁 Group: {smsData.groupName || "Unnamed Group"}
                    </span>
                    <span className="text-gray-600 font-medium">
                      ({smsData.msisdns?.length} recipients)
                    </span>
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  {smsData.msisdns?.map((number, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-1 px-2 py-1 bg-white rounded-lg border hover:shadow-sm transition-shadow"
                    >
                      <Phone size={12} className="text-gray-500" />
                      <span className="font-mono text-xs font-medium">
                        {number}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Message Content */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4 border">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 flex justify-center items-center bg-gray-600 rounded-lg">
                  <FileText className="text-white" size={14} />
                </div>
                <span className="font-semibold text-gray-900 text-sm">
                  Message Content
                </span>
              </div>
              <div className="bg-white rounded-lg p-3 border shadow-sm">
                <p className="text-gray-800 leading-relaxed text-sm">
                  {smsData.message.startsWith("http") ? (
                    <a
                      href={smsData.message}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 underline hover:text-blue-800 font-medium break-all"
                    >
                      {smsData.message}
                    </a>
                  ) : (
                    smsData.message
                  )}
                </p>
              </div>
            </div>

            {/* Timing Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 flex justify-center items-center bg-purple-500 rounded-lg">
                    <Calendar className="text-white" size={14} />
                  </div>
                  <span className="text-xs font-semibold text-purple-900 uppercase tracking-wide">
                    Created At
                  </span>
                </div>
                <div className="text-xs text-purple-700 font-medium bg-white px-3 py-1.5 rounded-lg border border-purple-200">
                  {formatDate(smsData.createdAt)}
                </div>
              </div>
              <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4 border border-orange-200">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 flex justify-center items-center bg-orange-500 rounded-lg">
                    <Send className="text-white" size={14} />
                  </div>
                  <span className="text-xs font-semibold text-orange-900 uppercase tracking-wide">
                    {status === "sent" ? "Delivered At" : "Scheduled For"}
                  </span>
                </div>
                <div className="text-xs text-orange-700 font-medium bg-white px-3 py-1.5 rounded-lg border border-orange-200">
                  {status === "sent"
                    ? formatDate(smsData.responseData?.ts)
                    : smsData.scheduleTime || "Immediate"}
                </div>
              </div>
            </div>

            {/* Delivery Information */}
            {smsData.responseData && (
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4 border">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 flex justify-center items-center bg-gray-600 rounded-lg">
                    <Info className="text-white" size={14} />
                  </div>
                  <span className="font-semibold text-gray-900 text-sm">
                    Delivery Information
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {/* Status */}
                  <div className="bg-white rounded-lg p-3 border">
                    <div className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1">
                      Status
                    </div>
                    <div
                      className={`font-semibold text-xs ${
                        smsData.responseData.status === "failure"
                          ? "text-red"
                          : "text-green"
                      }`}
                    >
                      {smsData.responseData.status}
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-3 border">
                    <div className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1">
                      Response Code
                    </div>
                    <div className="font-semibold text-gray-700 text-xs">
                      {smsData.responseData.code}
                    </div>
                  </div>

                  {smsData.responseData.status === "success" ? (
                    <>
                      <div className="bg-white rounded-lg p-3 border">
                        <div className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1">
                          Numbers Submitted
                        </div>
                        <div className="font-semibold text-gray-700 text-xs">
                          {smsData.responseData.totalnumbers_sbmited}
                        </div>
                      </div>

                      <div className="bg-white rounded-lg p-3 border">
                        <div className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1">
                          Log ID
                        </div>
                        <div className="font-mono text-xs text-gray-700 break-all">
                          {smsData.responseData.logid}
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="bg-white rounded-lg p-3 border">
                        <div className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1">
                          Description
                        </div>
                        <div className="font-semibold text-red-600 text-xs">
                          {smsData.responseData.desc}
                        </div>
                      </div>
                      <div className="bg-white rounded-lg p-3 border">
                        <div className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1">
                          Timestamp
                        </div>
                        <div className="font-mono text-xs text-gray-700">
                          {smsData.responseData.ts}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Component
export const SmsSchedleList = ({
  schedule,
  hasMore,
  totalCount,
  onLoadMore,
}) => {
  const [historyStack, setHistoryStack] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSms, setSelectedSms] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const pageSize = 10;
  const currentPage = historyStack.length;
  const start = currentPage * pageSize + 1;
  const end = start + schedule.length - 1;

  const handleNext = async () => {
    if (!schedule.length) return;
    const lastId = schedule[schedule.length - 1]._id;

    setLoading(true);
    setHistoryStack((prev) => [...prev, schedule[0]._id]);
    await onLoadMore("next", lastId);
    setLoading(false);
  };

  const handlePrev = async () => {
    if (!historyStack.length) return;
    const prevCursorId = historyStack[historyStack.length - 1];

    setLoading(true);
    await onLoadMore("prev", prevCursorId);
    setHistoryStack((prev) => prev.slice(0, -1));
    setLoading(false);
  };

  const handleViewDetails = (sms) => {
    setSelectedSms(sms);
    setIsModalOpen(true);
  };

  const getStatus = (item) => {
    if (item.type === "now") return "sent";
    if (item.type === "schedule") return "pending";
    return "unknown";
  };

  const statusIcon = (status) => {
    switch (status) {
      case "sent":
        return <BadgeCheck className="text-emerald-600" size={16} />;
      case "pending":
        return <Hourglass className="text-amber-500" size={16} />;
      case "failed":
        return <AlertTriangle className="text-red-500" size={16} />;
      default:
        return null;
    }
  };

  const statusColor = (status) => {
    switch (status) {
      case "sent":
        return "text-emerald-700 bg-emerald-50 border-emerald-200";
      case "pending":
        return "text-amber-700 bg-amber-50 border-amber-200";
      case "failed":
        return "text-red-700 bg-red-50 border-red-200";
      default:
        return "text-gray-700 bg-gray-50 border-gray-200";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  return (
    <>
      <div
        className="flex flex-col border border-[#c7d2f5] rounded-2xl bg-white overflow-hidden text-sm"
        style={{ boxShadow: "0px 4px 24px rgba(67, 98, 193, 0.12)" }}
      >
        {schedule.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full py-16 px-4">
            <div className="p-4 bg-[#e7ebfb] rounded-full mb-4">
              <MessageSquare className="text-[#b3c0f3]" size={32} />
            </div>
            <p className="text-[#4F22D8] font-semibold">
              No scheduled messages
            </p>
            <p className="text-[#8a99db] mt-1">
              Your scheduled messages will appear here
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-y-auto">
              <table className="w-full text-[#696969]">
                <thead className="sticky top-0 z-10 bg-[#4F22D8] shadow-sm">
                  <tr className="text-white">
                    <th className="px-6 py-4 text-left font-semibold tracking-wide border-l first:border-l-0 border-gray-300">
                      <div className="flex items-center gap-2">
                        <MessageSquare size={16} className="text-white/70" />
                        Template ID
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left font-semibold tracking-wide border-l border-gray-300">
                      <div className="flex items-center gap-2">
                        <Clock size={16} className="text-white/70" />
                        Status
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left font-semibold tracking-wide border-l border-gray-300">
                      <div className="flex items-center gap-2">
                        <Send size={16} className="text-white/70" />
                        Delivered At
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left font-semibold tracking-wide border-l border-gray-300">
                      <div className="flex items-center gap-2">
                        <CalendarDays size={16} className="text-white/70" />
                        Created At
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left font-semibold tracking-wide border-l border-gray-300">
                      <div className="flex items-center gap-2">
                        <MessageSquare size={16} className="text-white/70" />
                        Sent To
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left font-semibold tracking-wide border-l border-gray-300">
                      <div className="flex items-center gap-2">
                        <Eye size={16} className="text-white/70" />
                        Actions
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {schedule.map((item) => {
                    const status = getStatus(item);
                    return (
                      <tr
                        key={item._id}
                        className="group hover:bg-[#f0f3fd] transition-colors duration-200 ease-in-out"
                      >
                        <td className="px-6 py-4 border-l first:border-l-0 border-gray-200">
                          <div className="font-mono font-semibold text-[#2e428b] bg-[#e7ebfb] px-3 py-1.5 rounded-lg inline-block">
                            {item.templateId}
                          </div>
                        </td>
                        <td className="px-6 py-4 border-l border-gray-200">
                          <div
                            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-2xl border font-medium ${statusColor(
                              status
                            )}`}
                          >
                            {statusIcon(status)}
                            <span className="capitalize">{status}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 border-l border-gray-200">
                          <span className="font-medium">
                            {status === "sent"
                              ? formatDate(item.responseData?.ts)
                              : item.scheduleTime || "-"}
                          </span>
                        </td>
                        <td className="px-6 py-4 border-l border-gray-200">
                          <span className="font-medium">
                            {formatDate(item.createdAt)}
                          </span>
                        </td>
                        <td className="px-6 py-4 border-l border-gray-200">
                          <span className="font-medium">
                            {item.isGroup ? (
                              <span className="text-[#2e428b]">
                                {item.groupName || "Group"} (
                                {item.msisdns?.length})
                              </span>
                            ) : (
                              <span className="text-[#696969]">
                                {item.msisdns?.[0]}
                              </span>
                            )}
                          </span>
                        </td>
                        <td className="px-6 whitespace-nowrap py-4 border-l border-gray-200">
                          <button
                            onClick={() => handleViewDetails(item)}
                            className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-[#4F22D8] hover:text-[#3a1ba3] hover:bg-[#f0f3fd] rounded-lg transition-colors duration-200"
                          >
                            <Eye size={14} />
                            View Details
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination Footer */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-white text-sm">
              <div className="text-gray-500">
                Showing <span className="font-medium">{start}</span>–{" "}
                <span className="font-medium">{end}</span> of{" "}
                <span className="font-medium">{totalCount}</span> messages
              </div>
              <div className="flex items-center gap-4">
                <button
                  onClick={handlePrev}
                  disabled={historyStack.length === 0 || loading}
                  className={`flex items-center gap-1 px-3 py-1 rounded-md ${
                    historyStack.length === 0 || loading
                      ? "text-gray-400 cursor-not-allowed"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  <ChevronLeft size={18} />
                  Previous
                </button>
                <button
                  onClick={handleNext}
                  disabled={!hasMore || loading}
                  className={`flex items-center gap-1 px-3 py-1 rounded-md ${
                    !hasMore || loading
                      ? "text-gray-400 cursor-not-allowed"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Next
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Modal */}
      <SmsDetailModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        smsData={selectedSms}
      />
    </>
  );
};
